/**
 * @voltxampere/engine — ERC rule: overheating (Sprint 59)
 *
 * Aşama 2 foundation rule — flags components whose steady-state
 * junction temperature crosses the datasheet `T_max` threshold given
 * the current DC operating point + thermal metadata table.
 *
 * The rule is **opt-in**: it is registered in ALL_RULES so the
 * existing runERC() orchestration works without code changes, but
 * its `check()` is a no-op when the caller passes `realisticMode:
 * false` (default) via the orchestrator's options. The web app
 * threads `realisticModeEnabled.value` through. This prevents
 * false-positive overheating warnings on legacy presets that were
 * authored before the thermal compute path existed.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { estimateJunctionTemperatures } from "../../thermal/temperature.js";
const DEFAULT_CONFIG = { enabled: false, ambientTempC: 25 };
let currentConfig = DEFAULT_CONFIG;
/** Configure the rule. Default state: disabled. */
export function setOverheatingRuleConfig(cfg) {
    currentConfig = { enabled: cfg.enabled, ambientTempC: cfg.ambientTempC ?? 25 };
}
export function getOverheatingRuleConfig() {
    return currentConfig;
}
export function resetOverheatingRuleConfigForTests() {
    currentConfig = DEFAULT_CONFIG;
}
export const overheatingRule = {
    id: "overheating",
    defaultSeverity: "warn",
    requiresDCResult: true,
    check(circuit, dcResult) {
        if (!currentConfig.enabled)
            return [];
        if (dcResult === undefined)
            return [];
        const ambient = currentConfig.ambientTempC ?? 25;
        const tempMap = estimateJunctionTemperatures(circuit, {
            nodeVoltages: dcResult.nodeVoltages,
            sourceCurrents: dcResult.sourceCurrents,
        }, { ambientTempC: ambient });
        const violations = [];
        for (const t of tempMap.values()) {
            if (!t.isOverheated)
                continue;
            violations.push({
                ruleId: "overheating",
                severity: "warn",
                i18nKey: "erc.overheating.message",
                suggestionKey: "erc.overheating.suggestion",
                meta: {
                    componentId: t.componentId,
                    componentType: t.type,
                    junctionTempC: t.junctionTempC,
                    tMaxC: t.tMaxC,
                    powerW: t.powerW,
                    rThermal: t.rThermal,
                },
            });
        }
        return violations;
    },
};
//# sourceMappingURL=overheating.js.map